import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import MainLayout from './layout/MainLayout';
import CallExperienceOverlay from './components/CallExperienceOverlay';
import IncomingCallPopup from './components/IncomingCallPopup';
import socket, { disconnectAuthenticatedSocket, setSocketAuthToken } from './socket';
import BASE_URL from './config/api';
import {
  initVoice,
  getDeviceStatus,
  muteCall,
  unmuteCall,
  disconnectCall,
  resetVoice,
} from './services/voice';
import OptInPage from './pages/OptInPage';
import LoginPage from './pages/LoginPage';
import InfoPage from './pages/InfoPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import {
  clearAuthSession,
  fetchCurrentUser,
  getEffectiveAgentId,
  getEffectiveRole,
  getStoredAuthToken,
  getStoredAuthUser,
  isPlatformAdminRole,
  loginRequest,
  storeAuthSession,
  updateMyAvatarRequest,
  updateMyAvailabilityStatusRequest,
} from './services/auth';
import { AVAILABILITY_STATUS_OPTIONS, normalizeAvailabilityStatus, resolveEffectiveAvailabilityStatus } from './utils/presence';
import {
  cancelCoalescedRefresh,
  createCoalescedRefreshState,
  disposeCoalescedRefresh,
  scheduleCoalescedRefresh,
} from './utils/coalescedRefresh';
import { getDesktopRouteRedirect, isDesktopMode } from './utils/desktopMode';
import { playChatNotificationBell, unlockChatNotificationAudio } from './utils/chatNotificationSound';
import {
  clearPersonalAvatarCache,
  configurePersonalAvatarSession,
  invalidatePersonalAvatarForUser,
} from './services/personalAvatarCache';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Calls = lazy(() => import('./pages/Calls'));
const Users = lazy(() => import('./pages/Users'));
const Messages = lazy(() => import('./pages/MessagesPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPortal = lazy(() => import('./pages/AdminPortal'));
const ClientPortal = lazy(() => import('./pages/ClientPortal'));
const ResellerPortal = lazy(() => import('./pages/ResellerPortal'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const CallQueueDashboard = lazy(() => import('./pages/CallQueueDashboard'));

const DIRECTORY_MANAGER_ROLES = new Set(['admin', 'platform_admin', 'reseller_admin', 'client_admin', 'client_user', 'agent']);
const REPORT_MANAGER_ROLES = new Set(['admin', 'platform_admin', 'client_admin']);
const CHAT_NOTIFICATION_SOUND_INTERVAL_MS = 1000;
const CHAT_NOTIFICATION_SOUND_DEDUPE_TTL_MS = 5 * 60 * 1000;
const isDirectoryManagerRole = (role) => DIRECTORY_MANAGER_ROLES.has(String(role || '').trim().toLowerCase());
const isReportManagerRole = (role) => REPORT_MANAGER_ROLES.has(String(role || '').trim().toLowerCase());
const buildInternalConversationKey = (conversationType, conversationId) => (
  `${String(conversationType || '').trim()}:${String(conversationId || '').trim()}`
);

export const DesktopRouteGuard = ({ desktopMode, isAuthenticated, children }) => {
  const location = useLocation();
  const redirectTo = getDesktopRouteRedirect(location.pathname, { desktopMode, isAuthenticated });
  return redirectTo ? <Navigate to={redirectTo} replace /> : children;
};

export const buildPresenceSocketPayloads = ({
  desktopMode,
  userId,
  availabilityStatus,
  isVoiceReady,
  deviceStatus,
}) => {
  const registration = {
    userId,
    status: 'online',
    availabilityStatus,
    ...(!desktopMode ? { voiceReady: isVoiceReady } : {}),
  };

  return {
    registration,
    voiceReadiness: desktopMode ? null : {
      userId,
      voiceReady: isVoiceReady,
      deviceStatus,
    },
  };
};

const isChatNotificationDebugEnabled = () => {
  if (process.env.NODE_ENV !== 'production') return true;
  if (typeof window === 'undefined') return false;

  try {
    return new URLSearchParams(window.location.search).get('notificationDebug') === '1';
  } catch (error) {
    return false;
  }
};

const logChatNotification = (event, details = {}) => {
  if (!isChatNotificationDebugEnabled()) return;
  console.debug('[CHAT_NOTIFICATION_SOUND]', event, details);
};

const isSocketAuthDebugEnabled = () => {
  if (process.env.NODE_ENV !== 'production') return true;
  if (typeof window === 'undefined') return false;

  try {
    return new URLSearchParams(window.location.search).get('socketDebug') === '1';
  } catch (error) {
    return false;
  }
};

const logSocketAuth = (event, details = {}) => {
  if (!isSocketAuthDebugEnabled()) return;
  console.debug('[SOCKET_AUTH]', event, details);
};

function App() {
  const appMountedRef = useRef(false);
  const lastRegisteredSocketIdentityRef = useRef(null);
  const sidebarUnreadRefreshStateRef = useRef(null);
  const messagesPageOwnsInternalConversationsRef = useRef(false);
  const activeInternalConversationKeyRef = useRef('');
  if (!sidebarUnreadRefreshStateRef.current) {
    sidebarUnreadRefreshStateRef.current = createCoalescedRefreshState();
  }
  const [authToken, setAuthToken] = useState(() => getStoredAuthToken());
  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());
  const [authChecking, setAuthChecking] = useState(() => Boolean(getStoredAuthToken()));
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');
  const [connection, setConnection] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState(() => getDeviceStatus());
  const [callState, setCallState] = useState('idle');
  const [callNotice, setCallNotice] = useState(null);
  const [callParticipant, setCallParticipant] = useState(null);
  const [userRole, setUserRole] = useState(() => getEffectiveRole(getStoredAuthUser()));
  const [agentStatus, setAgentStatus] = useState('offline');
  const [availabilityStatus, setAvailabilityStatus] = useState(() => normalizeAvailabilityStatus(getStoredAuthUser()?.availabilityStatus || 'online'));
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState('');
  const [agentId, setAgentId] = useState(() => getEffectiveAgentId(getStoredAuthUser()));
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [avatarUpdateError, setAvatarUpdateError] = useState('');
  const [sidebarUnreadTotals, setSidebarUnreadTotals] = useState(() => ({
    internalChat: 0,
    internalTeams: 0,
  }));
  const desktopMode = isDesktopMode();

  const isAuthenticated = Boolean(authToken && authUser);
  const authenticatedAgentId = authUser?.agentId || '';
  const workspaceAgentId = isAuthenticated
    ? (authenticatedAgentId || agentId || 'web_user')
    : (agentId || 'web_user');
  const isVoiceReady = deviceStatus === 'ready';

  useEffect(() => {
    configurePersonalAvatarSession({ token: authToken, user: authUser });
  }, [authToken, authUser]);

  const syncSidebarUnreadTotals = useCallback((conversations = []) => {
    setSidebarUnreadTotals(calculateSidebarUnreadTotals(conversations));
  }, []);

  const fetchSidebarUnreadTotals = useCallback(async () => {
    if (!isAuthenticated) {
      if (appMountedRef.current) {
        setSidebarUnreadTotals({ internalChat: 0, internalTeams: 0 });
      }
      return;
    }

    try {
      const params = new URLSearchParams({
        role: userRole,
        userId: workspaceAgentId,
      });
      const token = getStoredAuthToken();
      const response = await fetch(`${BASE_URL}/api/messages/conversations?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to fetch internal unread totals');
      }

      const data = await response.json();
      if (!appMountedRef.current) return;
      syncSidebarUnreadTotals(data || []);
    } catch (error) {
      console.error('Sidebar unread totals error:', error);
    }
  }, [isAuthenticated, syncSidebarUnreadTotals, userRole, workspaceAgentId]);

  const scheduleSidebarUnreadTotalsRefresh = useCallback((options = {}) => {
    scheduleCoalescedRefresh(
      sidebarUnreadRefreshStateRef.current,
      fetchSidebarUnreadTotals,
      options
    );
  }, [fetchSidebarUnreadTotals]);

  const handleInternalConversationsOwnershipChange = useCallback((isOwned) => {
    const nextIsOwned = Boolean(isOwned);
    const wasOwned = messagesPageOwnsInternalConversationsRef.current;
    messagesPageOwnsInternalConversationsRef.current = nextIsOwned;

    if (wasOwned && !nextIsOwned && isAuthenticated) {
      scheduleSidebarUnreadTotalsRefresh({
        immediate: true,
      });
    }
  }, [isAuthenticated, scheduleSidebarUnreadTotalsRefresh]);

  const handleActiveInternalConversationChange = useCallback((conversationKey) => {
    const normalizedKey = String(conversationKey || '').trim();
    activeInternalConversationKeyRef.current = (
      normalizedKey.startsWith('internal_dm:') || normalizedKey.startsWith('team:')
    ) ? normalizedKey : '';
  }, []);

  useEffect(() => {
    appMountedRef.current = true;
    if (!sidebarUnreadRefreshStateRef.current || sidebarUnreadRefreshStateRef.current.disposed) {
      sidebarUnreadRefreshStateRef.current = createCoalescedRefreshState();
    }

    return () => {
      appMountedRef.current = false;
      disposeCoalescedRefresh(sidebarUnreadRefreshStateRef.current);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const storedToken = getStoredAuthToken();

      if (!storedToken) {
        if (isMounted) {
          setAuthToken(null);
          setAuthUser(null);
          setAuthChecking(false);
        }
        return;
      }

      try {
        const payload = await fetchCurrentUser(storedToken);
        if (!isMounted) return;

        const user = payload?.user || null;
        storeAuthSession({ token: storedToken, user });
        setAuthToken(storedToken);
        setAuthUser(user);
        setAuthError('');
      } catch (error) {
        if (!isMounted) return;

        clearAuthSession();
        setAuthToken(null);
        setAuthUser(null);
      } finally {
        if (isMounted) {
          setAuthChecking(false);
        }
      }
    };

    restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let disposed = false;
    let handleUserInteraction = null;
    const removeUnlockListeners = () => {
      if (!handleUserInteraction) return;
      window.removeEventListener('pointerdown', handleUserInteraction, true);
      window.removeEventListener('keydown', handleUserInteraction, true);
    };
    handleUserInteraction = async () => {
      const result = await unlockChatNotificationAudio();
      if (!disposed && result?.ok) {
        removeUnlockListeners();
      }
    };

    window.addEventListener('pointerdown', handleUserInteraction, true);
    window.addEventListener('keydown', handleUserInteraction, true);

    return () => {
      disposed = true;
      removeUnlockListeners();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let disposed = false;
    let playbackTimer = null;
    let playbackInProgress = false;
    let lastPlayedAt = 0;
    const handledMessageIds = new Map();
    const pendingMessageIds = new Set();
    const playbackQueue = [];

    const pruneHandledMessageIds = (now = Date.now()) => {
      handledMessageIds.forEach((handledAt, messageId) => {
        if (now - handledAt > CHAT_NOTIFICATION_SOUND_DEDUPE_TTL_MS) {
          handledMessageIds.delete(messageId);
        }
      });
    };

    const markHandled = (messageId) => {
      if (messageId) handledMessageIds.set(messageId, Date.now());
    };

    const scheduleNextPlayback = () => {
      if (disposed || playbackInProgress || playbackTimer || playbackQueue.length === 0) return;
      const delay = Math.max(0, CHAT_NOTIFICATION_SOUND_INTERVAL_MS - (Date.now() - lastPlayedAt));

      playbackTimer = window.setTimeout(async () => {
        playbackTimer = null;
        const messageId = playbackQueue.shift();
        if (!messageId || disposed) return;

        playbackInProgress = true;
        const result = await playChatNotificationBell();
        playbackInProgress = false;
        pendingMessageIds.delete(messageId);

        if (result?.status === 'played') {
          lastPlayedAt = Date.now();
          markHandled(messageId);
          logChatNotification('sound_played', { messageId });
        } else if (result?.status === 'blocked') {
          logChatNotification('autoplay_blocked', {
            messageId,
            reason: result.reason,
            errorName: result.errorName,
          });
        } else {
          logChatNotification('playback_failed', {
            messageId,
            reason: result?.reason || 'unknown',
            errorName: result?.errorName,
          });
        }

        scheduleNextPlayback();
      }, delay);
    };

    const suppress = (messageId, reason) => {
      markHandled(messageId);
      logChatNotification('suppressed', { messageId, reason });
    };

    const handleIncomingInternalMessage = (message) => {
      const conversationType = String(message?.conversationType || '').trim();
      if (conversationType !== 'internal_dm' && conversationType !== 'team') return;

      const userId = String(workspaceAgentId || '').trim();
      const senderId = String(message?.senderId || '').trim();
      const messageId = String(message?._id || message?.id || '').trim();
      const conversationId = String(message?.conversationId || '').trim();
      if (!userId || !senderId || !messageId || !conversationId) {
        logChatNotification('suppressed', { messageId, reason: 'missing_identity' });
        return;
      }

      const now = Date.now();
      pruneHandledMessageIds(now);
      if (handledMessageIds.has(messageId) || pendingMessageIds.has(messageId)) {
        logChatNotification('suppressed', { messageId, reason: 'duplicate' });
        return;
      }
      if (senderId === userId) {
        suppress(messageId, 'own_message');
        return;
      }

      const latestUser = getStoredAuthUser();
      const mutedTeamIds = new Set(
        (Array.isArray(latestUser?.mutedTeamChatIds) ? latestUser.mutedTeamChatIds : [])
          .map((value) => String(value || '').trim())
          .filter(Boolean)
      );
      if (conversationType === 'team' && mutedTeamIds.has(conversationId)) {
        suppress(messageId, 'team_muted');
        return;
      }

      const participants = Array.isArray(message?.participants)
        ? message.participants.map((participant) => String(participant || '').trim()).filter(Boolean)
        : [];
      if (participants.length > 0 && !participants.includes(userId)) {
        suppress(messageId, 'not_a_participant');
        return;
      }
      if (latestUser?.chatNotificationSoundEnabled === false) {
        suppress(messageId, 'preference_disabled');
        return;
      }

      const conversationKey = buildInternalConversationKey(conversationType, conversationId);
      const isFocusedActiveConversation = conversationKey === activeInternalConversationKeyRef.current
        && document.visibilityState === 'visible'
        && typeof document.hasFocus === 'function'
        && document.hasFocus();
      if (isFocusedActiveConversation) {
        suppress(messageId, 'focused_active_conversation');
        return;
      }

      logChatNotification('eligible', { messageId, conversationType, conversationId });
      pendingMessageIds.add(messageId);
      playbackQueue.push(messageId);
      scheduleNextPlayback();
    };

    socket.on('newMessage', handleIncomingInternalMessage);
    return () => {
      disposed = true;
      if (playbackTimer) window.clearTimeout(playbackTimer);
      socket.off('newMessage', handleIncomingInternalMessage);
      playbackQueue.length = 0;
      pendingMessageIds.clear();
      handledMessageIds.clear();
    };
  }, [isAuthenticated, workspaceAgentId]);

  useEffect(() => {
    if (!authUser) return;

    setUserRole(getEffectiveRole(authUser));
    setAgentId(authUser.agentId || '');
    setAvailabilityStatus(normalizeAvailabilityStatus(authUser.availabilityStatus || 'online'));
  }, [authUser]);

  useEffect(() => {
    if (!isAuthenticated || !authToken) {
      disconnectAuthenticatedSocket();
      lastRegisteredSocketIdentityRef.current = null;
      return undefined;
    }

    const previousToken = String(socket.auth?.token || '').trim();
    const tokenChanged = previousToken !== authToken;
    setSocketAuthToken(authToken);

    const handleConnect = () => {
      logSocketAuth(tokenChanged ? 'connected_with_updated_auth' : 'authenticated_connection_ready');
    };
    const handleConnectError = (error) => {
      const code = String(error?.data?.code || '').trim();
      logSocketAuth('connection_error', { code: code || 'SOCKET_CONNECT_ERROR' });
      if (code === 'SOCKET_AUTH_FAILED') {
        socket.disconnect();
      }
    };
    const handleIdentityError = (payload) => {
      logSocketAuth('identity_mismatch', { code: payload?.code || 'SOCKET_IDENTITY_MISMATCH' });
    };
    const handleReconnectAttempt = () => {
      setSocketAuthToken(authToken);
      logSocketAuth('reconnect_with_auth');
    };

    socket.on('connect', handleConnect);
    socket.on('connect_error', handleConnectError);
    socket.on('socketIdentityError', handleIdentityError);
    socket.io.on('reconnect_attempt', handleReconnectAttempt);

    if (tokenChanged && socket.connected) {
      socket.disconnect();
    }
    if (!socket.connected) {
      socket.connect();
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleConnectError);
      socket.off('socketIdentityError', handleIdentityError);
      socket.io.off('reconnect_attempt', handleReconnectAttempt);
    };
  }, [authToken, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const userId = workspaceAgentId;

    if (!socket || !userId) return;

    const registerIdentity = () => {
      setAgentStatus('online');
      const payloads = buildPresenceSocketPayloads({
        desktopMode,
        userId,
        availabilityStatus,
        isVoiceReady,
        deviceStatus,
      });
      socket.emit('registerUser', payloads.registration);
      if (payloads.voiceReadiness) {
        socket.emit('voiceReady', payloads.voiceReadiness);
      }
      lastRegisteredSocketIdentityRef.current = userId;
      console.log('Registered socket user:', userId, {
        agentStatus: 'online',
        availabilityStatus,
        ...(!desktopMode ? { deviceStatus, voiceReady: isVoiceReady } : {}),
      });
    };

    const handleDisconnect = () => {
      setAgentStatus('offline');
    };

    socket.on('connect', registerIdentity);
    socket.on('disconnect', handleDisconnect);

    if (socket.connected) {
      registerIdentity();
    }

    return () => {
      socket.off('connect', registerIdentity);
      socket.off('disconnect', handleDisconnect);
    };
  }, [availabilityStatus, desktopMode, deviceStatus, isAuthenticated, isVoiceReady, workspaceAgentId]);

  useEffect(() => {
    if (!isAuthenticated) {
      cancelCoalescedRefresh(sidebarUnreadRefreshStateRef.current);
      setSidebarUnreadTotals({ internalChat: 0, internalTeams: 0 });
      return;
    }

    scheduleSidebarUnreadTotalsRefresh({ immediate: true });
  }, [isAuthenticated, scheduleSidebarUnreadTotalsRefresh]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const refreshInternalUnreadTotals = (payload) => {
      const conversationType = String(payload?.conversationType || '').trim();
      if (!conversationType || (conversationType !== 'internal_dm' && conversationType !== 'team')) {
        return;
      }

      if (messagesPageOwnsInternalConversationsRef.current) {
        return;
      }

      scheduleSidebarUnreadTotalsRefresh();
    };

    const handleNewMessage = (payload) => refreshInternalUnreadTotals(payload);
    const handleInternalMessageStatus = (payload) => refreshInternalUnreadTotals(payload);
    const handleInternalMessageUpdated = (payload) => refreshInternalUnreadTotals(payload);
    const handleInternalMessageDeleted = (payload) => refreshInternalUnreadTotals(payload);

    socket.on('newMessage', handleNewMessage);
    socket.on('internalMessageStatus', handleInternalMessageStatus);
    socket.on('internalMessageUpdated', handleInternalMessageUpdated);
    socket.on('internalMessageDeleted', handleInternalMessageDeleted);

    return () => {
      socket.off('newMessage', handleNewMessage);
      socket.off('internalMessageStatus', handleInternalMessageStatus);
      socket.off('internalMessageUpdated', handleInternalMessageUpdated);
      socket.off('internalMessageDeleted', handleInternalMessageDeleted);
    };
  }, [isAuthenticated, scheduleSidebarUnreadTotalsRefresh]);

  useEffect(() => {
    if (!isAuthenticated || !authToken) return undefined;

    const handleUserPresenceUpdated = (payload) => {
      const nextAgentId = String(payload?.userId || payload?.agentId || '').trim();
      if (!nextAgentId || nextAgentId !== workspaceAgentId) {
        return;
      }

      const nextAvailabilityStatus = normalizeAvailabilityStatus(
        payload?.availabilityStatus || availabilityStatus
      );
      const nextPresenceStatus = String(payload?.presenceStatus || '').trim().toLowerCase() || 'offline';

      setAgentStatus(nextPresenceStatus);
      setAvailabilityStatus(nextAvailabilityStatus);
      setAuthUser((current) => {
        if (!current) return current;

        const nextUser = {
          ...current,
          availabilityStatus: nextAvailabilityStatus,
        };
        storeAuthSession({ token: authToken, user: nextUser });
        return nextUser;
      });
    };

    const handleUserAvatarUpdated = (payload) => {
      const nextUser = payload?.user || null;
      const nextUserId = String(nextUser?.id || nextUser?._id || payload?.userId || '').trim();
      const currentUserDbId = String(authUser?.id || authUser?._id || '').trim();

      if (!nextUserId || !currentUserDbId || nextUserId !== currentUserDbId) {
        return;
      }

      invalidatePersonalAvatarForUser(nextUserId);
      setAuthUser((current) => {
        if (!current) return current;
        const updatedUser = nextUser || {
          ...current,
          avatarUrl: payload?.avatarRemoved ? '' : String(payload?.avatarUrl || ''),
        };
        storeAuthSession({ token: authToken, user: updatedUser });
        return updatedUser;
      });
    };

    socket.on('userPresenceUpdated', handleUserPresenceUpdated);
    socket.on('userAvatarUpdated', handleUserAvatarUpdated);
    socket.on('userAvatarReferenceUpdated', handleUserAvatarUpdated);
    return () => {
      socket.off('userPresenceUpdated', handleUserPresenceUpdated);
      socket.off('userAvatarUpdated', handleUserAvatarUpdated);
      socket.off('userAvatarReferenceUpdated', handleUserAvatarUpdated);
    };
  }, [authToken, authUser?.id, authUser?._id, availabilityStatus, isAuthenticated, workspaceAgentId]);

  useEffect(() => {
    if (!isAuthenticated || desktopMode) return undefined;

    const startVoice = async () => {
      const userId = workspaceAgentId || '';
      await initVoice(userId || undefined);
    };

    window.addEventListener('click', startVoice, { once: true });

    return () => {
      window.removeEventListener('click', startVoice);
    };
  }, [desktopMode, isAuthenticated, workspaceAgentId]);

  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail;
      const conn = payload?.connection || payload;
      setConnection(conn);
      setCallNotice(null);
      if (payload?.party) {
        setCallParticipant(normalizeCallParticipant(payload.party));
        if (payload.party.direction === 'incoming') {
          setCallState('connecting');
        }
      }

      conn.on('disconnect', () => {
        setConnection(null);
        setIsMuted(false);
      });
    };

    window.addEventListener('callAccepted', handler);

    return () => window.removeEventListener('callAccepted', handler);
  }, []);

  useEffect(() => {
    const handleDeviceStatus = (e) => {
      setDeviceStatus(e.detail?.status || 'offline');
    };

    const handleCallState = (e) => {
      const nextState = e.detail?.state || 'idle';
      setCallState(nextState);

      if (['missed', 'failed', 'ended'].includes(nextState)) {
        setCallNotice(getCallNotice(nextState));
      } else {
        setCallNotice(null);
      }
    };

    const handleCallEnded = () => {
      setConnection(null);
      setIsMuted(false);
    };

    window.addEventListener('voiceDeviceStatus', handleDeviceStatus);
    window.addEventListener('voiceCallState', handleCallState);
    window.addEventListener('callEnded', handleCallEnded);

    return () => {
      window.removeEventListener('voiceDeviceStatus', handleDeviceStatus);
      window.removeEventListener('voiceCallState', handleCallState);
      window.removeEventListener('callEnded', handleCallEnded);
    };
  }, []);

  useEffect(() => {
    if (!callNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setCallNotice(null);
      setCallState('idle');
      setCallParticipant(null);
    }, 2500);

    return () => window.clearTimeout(timeoutId);
  }, [callNotice]);

  useEffect(() => {
    const handleIncomingCall = (data) => {
      setCallState('incoming');
      setCallParticipant(normalizeCallParticipant({
        name: [data?.contact?.firstName, data?.contact?.lastName].filter(Boolean).join(' ').trim(),
        number: data?.from || '',
        label: data?.contact?.dba || '',
        direction: 'incoming',
        callId: data?.callSid || '',
        routeType: data?.routeType || data?.callType || '',
      }));
    };

    const handleSocketCallStatus = (data) => {
      const mappedStatus = mapCallStatus(data?.status);
      if (mappedStatus) {
        setCallState(mappedStatus);
      }
    };

    const handleSocketCallEnded = () => {
      setCallState((prev) => (prev === 'failed' || prev === 'missed' ? prev : 'ended'));
    };

    const handleOutgoingMeta = (event) => {
      setCallParticipant(normalizeCallParticipant({
        name: event.detail?.phone || '',
        number: event.detail?.phone || '',
        label: 'Outbound call',
        direction: 'outgoing',
      }));
      setCallNotice(null);
    };

    socket.on('incomingCall', handleIncomingCall);
    socket.on('callStatus', handleSocketCallStatus);
    socket.on('callEnded', handleSocketCallEnded);
    window.addEventListener('voiceOutgoingCall', handleOutgoingMeta);

    return () => {
      socket.off('incomingCall', handleIncomingCall);
      socket.off('callStatus', handleSocketCallStatus);
      socket.off('callEnded', handleSocketCallEnded);
      window.removeEventListener('voiceOutgoingCall', handleOutgoingMeta);
    };
  }, []);

  const hangUp = () => {
    disconnectCall();
    setConnection(null);
    setCallState('ended');
  };

  const toggleMute = () => {
    if (!isMuted) {
      muteCall();
    } else {
      unmuteCall();
    }
    setIsMuted(!isMuted);
  };

  const handleAvailabilityStatusChange = async (nextStatus) => {
    if (!isAuthenticated || !authToken || isUpdatingStatus) return;

    const normalizedStatus = normalizeAvailabilityStatus(nextStatus);
    const previousStatus = availabilityStatus;
    setStatusUpdateError('');
    setIsUpdatingStatus(true);
    setAvailabilityStatus(normalizedStatus);

    try {
      const payload = await updateMyAvailabilityStatusRequest(authToken, normalizedStatus);
      const nextUser = payload?.user || null;
      if (!nextUser) {
        throw new Error('Status update did not return a user payload');
      }

      storeAuthSession({ token: authToken, user: nextUser });
      setAuthUser(nextUser);
      setAvailabilityStatus(normalizeAvailabilityStatus(nextUser.availabilityStatus || normalizedStatus));
    } catch (error) {
      console.error('Update availability status failed:', error);
      setAvailabilityStatus(previousStatus);
      setStatusUpdateError(error?.message || 'Unable to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAgentChange = async (nextAgent) => {
    if (isAuthenticated) return;

    const userId = nextAgent || 'web_user';
    setAgentId(userId);
    window.localStorage?.setItem('voiceUserId', userId);
    await initVoice(userId);
  };

  const handleRetryVoice = async () => {
    if (!isAuthenticated) return;
    await initVoice(workspaceAgentId || undefined);
  };

  const handleRoleChange = (nextRole) => {
    if (authUser) return;

    const normalized = nextRole === 'agent' ? 'agent' : 'admin';
    setUserRole(normalized);
    window.localStorage?.setItem('userRole', normalized);
  };

  const handleLogin = async ({ email, password }) => {
    try {
      setAuthSubmitting(true);
      setAuthError('');

      const payload = await loginRequest({ email, password });
      const token = payload?.token || '';
      const user = payload?.user || null;

      storeAuthSession({ token, user });
      setAuthToken(token);
      setAuthUser(user);
      setUserRole(getEffectiveRole(user));
      setAvailabilityStatus(normalizeAvailabilityStatus(user?.availabilityStatus || 'online'));
      setAgentId(user?.agentId || '');
    } catch (error) {
      setAuthError(error.message || 'Login failed');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearPersonalAvatarCache();
    clearAuthSession();
    disconnectAuthenticatedSocket();
    lastRegisteredSocketIdentityRef.current = null;
    resetVoice();
    setAuthToken(null);
    setAuthUser(null);
    setAuthError('');
    setConnection(null);
    setIsMuted(false);
    setCallState('idle');
      setCallNotice(null);
      setCallParticipant(null);
      setUserRole('admin');
      setAgentStatus('offline');
      setAvailabilityStatus('online');
      setIsUpdatingStatus(false);
      setStatusUpdateError('');
      setIsUpdatingAvatar(false);
      setAvatarUpdateError('');
      setAgentId('web_user');
  };

  const handleAvatarChange = async (avatarDataUrl) => {
    if (!isAuthenticated || !authToken || isUpdatingAvatar) return;

    try {
      setIsUpdatingAvatar(true);
      setAvatarUpdateError('');

      const payload = await updateMyAvatarRequest(authToken, avatarDataUrl);
      const nextUser = payload?.user || null;

      if (!nextUser) {
        throw new Error('Avatar update did not return a user payload');
      }

      invalidatePersonalAvatarForUser(nextUser.id || nextUser._id);
      storeAuthSession({ token: authToken, user: nextUser });
      setAuthUser(nextUser);
    } catch (error) {
      console.error('Update avatar failed:', error);
      setAvatarUpdateError(error?.message || 'Unable to update avatar');
      throw error;
    } finally {
      setIsUpdatingAvatar(false);
    }
  };

  const handleProfileUpdated = (nextUser) => {
    if (!nextUser) return;

    storeAuthSession({ token: authToken, user: nextUser });
    setAuthUser(nextUser);
  };

  const renderProtectedLayout = (children, { adminOnly = false, directoryOnly = false, reportsOnly = false } = {}) => {
    if (authChecking) {
      return <div style={authLoadingStyle}>Restoring your session...</div>;
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (adminOnly && !isPlatformAdminRole(authUser?.role || userRole)) {
      return <Navigate to="/messages" replace />;
    }

    if (directoryOnly && !isDirectoryManagerRole(authUser?.role || userRole)) {
      return <Navigate to="/messages" replace />;
    }

    if (reportsOnly && !isReportManagerRole(authUser?.role || userRole)) {
      return <Navigate to="/messages" replace />;
    }

    return (
      <MainLayout
        isDesktopMode={desktopMode}
        userRole={userRole}
        onRoleChange={handleRoleChange}
        roleLocked={Boolean(authUser)}
        sidebarUnreadTotals={sidebarUnreadTotals}
        authUser={authUser}
        onLogout={handleLogout}
        deviceStatus={deviceStatus}
        callState={callState}
        agentId={workspaceAgentId}
        agentStatus={resolveEffectiveAvailabilityStatus({
          connected: agentStatus === 'online',
          availabilityStatus,
        })}
        onRetryVoice={handleRetryVoice}
        availabilityStatus={availabilityStatus}
        availabilityOptions={AVAILABILITY_STATUS_OPTIONS}
        onAvailabilityStatusChange={handleAvailabilityStatusChange}
        isUpdatingStatus={isUpdatingStatus}
        statusUpdateError={statusUpdateError}
        onAvatarChange={handleAvatarChange}
        isUpdatingAvatar={isUpdatingAvatar}
        avatarUpdateError={avatarUpdateError}
      >
        <Suspense fallback={<div style={routeLoadingStyle}>Loading...</div>}>
          {children}
        </Suspense>
      </MainLayout>
    );
  };

  return (
    <div className="App">
      {isAuthenticated && !desktopMode ? <IncomingCallPopup /> : null}

      {isAuthenticated && !desktopMode && callNotice ? (
        <div style={noticeStyle}>
          {callNotice}
        </div>
      ) : null}

      {isAuthenticated && !desktopMode && shouldRenderCallOverlay({ callState, connection, callParticipant }) ? (
        <CallExperienceOverlay
          callState={callState}
          participant={callParticipant}
          isMuted={isMuted}
          onToggleMute={toggleMute}
          onHangUp={hangUp}
        />
      ) : null}

      <BrowserRouter>
        <DesktopRouteGuard desktopMode={desktopMode} isAuthenticated={isAuthenticated}>
          <Routes>
          <Route
            path="/login"
            element={
              authChecking ? (
                <div style={authLoadingStyle}>Restoring your session...</div>
              ) : isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <LoginPage
                  onLogin={handleLogin}
                  isSubmitting={authSubmitting}
                  error={authError}
                />
              )
            }
          />
          <Route
            path="/"
            element={
              renderProtectedLayout(
                desktopMode ? (
                  <Navigate to="/internal-chat" replace />
                ) : isPlatformAdminRole(authUser?.role || userRole) ? (
                  <Dashboard
                    agentId={workspaceAgentId}
                    onAgentChange={handleAgentChange}
                    agentSelectionLocked={!isPlatformAdminRole(authUser?.role)}
                  />
                ) : (
                  <Navigate to="/internal-chat" replace />
                )
              )
            }
          />
          <Route
            path="/messages"
            element={<Navigate to={desktopMode ? '/internal-chat' : '/sms-mms'} replace />}
          />
          <Route
            path="/internal-chat"
            element={renderProtectedLayout(
              <Messages
                currentRole={userRole}
                currentUserId={workspaceAgentId}
                viewMode="internal"
                onInternalConversationsChange={syncSidebarUnreadTotals}
                onInternalConversationsOwnershipChange={handleInternalConversationsOwnershipChange}
                onActiveInternalConversationChange={handleActiveInternalConversationChange}
              />
            )}
          />
          <Route
            path="/internal-teams"
            element={renderProtectedLayout(
              <Messages
                currentRole={userRole}
                currentUserId={workspaceAgentId}
                viewMode="teams"
                onInternalConversationsChange={syncSidebarUnreadTotals}
                onInternalConversationsOwnershipChange={handleInternalConversationsOwnershipChange}
                onActiveInternalConversationChange={handleActiveInternalConversationChange}
              />
            )}
          />
          <Route
            path="/sms-mms"
            element={desktopMode ? renderProtectedLayout(<Navigate to="/internal-chat" replace />) : renderProtectedLayout(
              <Messages
                currentRole={userRole}
                currentUserId={workspaceAgentId}
                viewMode="customers"
                onInternalConversationsChange={syncSidebarUnreadTotals}
                onInternalConversationsOwnershipChange={handleInternalConversationsOwnershipChange}
                onActiveInternalConversationChange={handleActiveInternalConversationChange}
              />
            )}
          />
          <Route
            path="/calls"
            element={desktopMode ? renderProtectedLayout(<Navigate to="/internal-chat" replace />) : renderProtectedLayout(<Calls />)}
          />
          <Route path="/call-queues" element={renderProtectedLayout(<CallQueueDashboard />)} />
          <Route
            path="/users"
            element={desktopMode ? renderProtectedLayout(<Navigate to="/internal-chat" replace />, { directoryOnly: true }) : renderProtectedLayout(
              <Users
                currentUserRole={authUser?.role || userRole}
                currentUserId={authUser?.id || ''}
              />,
              { directoryOnly: true }
            )}
          />
          <Route
            path="/settings"
            element={renderProtectedLayout(
              <SettingsPage
                currentUserRole={authUser?.role || userRole}
                currentUserId={authUser?.id || ''}
              />,
              { adminOnly: true }
            )}
          />
          <Route
            path="/settings/reports"
            element={renderProtectedLayout(
              <SettingsPage
                currentUserRole={authUser?.role || userRole}
                currentUserId={authUser?.id || ''}
                reportOnly
              />,
              { reportsOnly: true }
            )}
          />
          <Route
            path="/admin-portal"
            element={renderProtectedLayout(
              <AdminPortal />,
              { adminOnly: true }
            )}
          />
          <Route
            path="/admin-portal/number-porting"
            element={renderProtectedLayout(
              <AdminPortal portingView="list" />,
              { adminOnly: true }
            )}
          />
          <Route
            path="/admin-portal/number-porting/new"
            element={renderProtectedLayout(
              <AdminPortal portingView="new" />,
              { adminOnly: true }
            )}
          />
          <Route
            path="/admin-portal/number-porting/:requestId"
            element={renderProtectedLayout(
              <AdminPortal portingView="detail" />,
              { adminOnly: true }
            )}
          />
          <Route
            path="/admin-portal/hosted-sms"
            element={renderProtectedLayout(
              <AdminPortal hostedSmsView="list" />,
              { adminOnly: true }
            )}
          />
          <Route
            path="/admin-portal/hosted-sms/new"
            element={renderProtectedLayout(
              <AdminPortal hostedSmsView="new" />,
              { adminOnly: true }
            )}
          />
          <Route
            path="/admin-portal/hosted-sms/:requestId"
            element={renderProtectedLayout(
              <AdminPortal hostedSmsView="detail" />,
              { adminOnly: true }
            )}
          />
          <Route
            path="/client-portal"
            element={renderProtectedLayout(<ClientPortal />)}
          />
          <Route
            path="/reseller-portal"
            element={renderProtectedLayout(<ResellerPortal />)}
          />
          <Route
            path="/profile"
            element={renderProtectedLayout(
              <UserProfile
                authToken={authToken}
                authUser={authUser}
                onProfileUpdated={handleProfileUpdated}
              />
            )}
          />
          <Route
            path="/numbers"
            element={<Navigate to="/settings" replace />}
          />
          <Route path="/info" element={<InfoPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/opt-in" element={<OptInPage />} />
          <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
          </Routes>
        </DesktopRouteGuard>
      </BrowserRouter>
    </div>
  );
}

const getCallNotice = (state) => {
  switch (state) {
    case 'missed':
      return 'Missed call';
    case 'failed':
      return 'Call failed';
    case 'ended':
      return 'Call ended';
    default:
      return '';
  }
};

const mapCallStatus = (status) => {
  switch (status) {
    case 'initiated':
      return 'connecting';
    case 'ringing':
      return 'ringing';
    case 'in-progress':
      return 'in-call';
    case 'busy':
    case 'failed':
      return 'failed';
    case 'no-answer':
      return 'missed';
    case 'completed':
    case 'canceled':
      return 'ended';
    default:
      return null;
  }
};

const noticeStyle = {
  position: 'fixed',
  right: '20px',
  bottom: '108px',
  zIndex: 9998,
  padding: '10px 14px',
  borderRadius: '12px',
  background: 'rgba(15, 23, 42, 0.92)',
  color: '#fff',
  boxShadow: '0 10px 24px rgba(15,23,42,0.22)',
  fontSize: '13px',
  fontWeight: 600,
};

const authLoadingStyle = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  background: 'linear-gradient(135deg, #eef4ff 0%, #f8fafc 55%, #eef6f0 100%)',
  color: '#0f172a',
  fontSize: '15px',
  fontWeight: 600,
};

const routeLoadingStyle = {
  width: '100%',
  minHeight: '320px',
  display: 'grid',
  placeItems: 'center',
  color: '#64748b',
  fontSize: '14px',
  fontWeight: 600,
};

export default App;

function calculateSidebarUnreadTotals(conversations = []) {
  return (Array.isArray(conversations) ? conversations : []).reduce(
    (totals, conversation) => {
      const conversationType = String(conversation?.conversationType || conversation?.type || '').trim();
      const unread = normalizeUnreadCount(conversation?.unreadCount ?? conversation?.unread);

      if (conversationType === 'internal_dm') {
        totals.internalChat += unread;
      } else if (conversationType === 'team') {
        totals.internalTeams += unread;
      }

      return totals;
    },
    { internalChat: 0, internalTeams: 0 }
  );
}

function normalizeUnreadCount(value) {
  const count = Number(value || 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

export function shouldRenderCallOverlay({ callState, connection, callParticipant }) {
  if (callState === 'in-call') return Boolean(connection);
  if (['connecting', 'ringing'].includes(callState)) {
    return Boolean(callParticipant) && callParticipant.direction !== 'incoming';
  }
  return false;
}

function normalizeCallParticipant(party) {
  const name = String(party?.name || '').trim();
  const number = String(party?.number || '').trim();
  const label = String(party?.label || '').trim();
  const callId = String(party?.callId || party?.callSid || '').trim();
  const routeType = String(party?.routeType || party?.callType || '').trim();

  return {
    name: name || formatPhone(number) || 'Unknown caller',
    number: formatPhone(number),
    label,
    direction: party?.direction === 'incoming' ? 'incoming' : 'outgoing',
    callId,
    routeType,
  };
}

function formatPhone(value) {
  const text = String(value || '').trim();
  const digits = text.replace(/\D/g, '');

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return text;
}
